import pako from 'pako';
import {
  deserializeCanonicalDataset,
  getCanonicalStorageId,
  serializeCanonicalDataset,
  validateCanonicalDataset,
} from '@canonical-process';

const DEFAULT_CHUNK_SIZE = 250000;

function uint8ArrayToBase64(bytes) {
  let binary = '';
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
}

function base64ToUint8Array(value) {
  const binary = atob(String(value || '').replace(/\s/g, ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function chunkString(value, size = DEFAULT_CHUNK_SIZE) {
  const result = [];
  for (let i = 0; i < value.length; i += size) result.push(value.slice(i, i + size));
  return result;
}

function normaliseChunkPayload(chunk) {
  let raw = chunk?.event_log ?? chunk?.EVENT_LOG ?? chunk?.array ?? '';
  if (raw && typeof raw === 'object') raw = raw.value ?? raw.display_value ?? '';
  return String(raw || '').replace(/\s/g, '');
}

function chunkNumber(chunk, index) {
  const raw = chunk?.chunk_no ?? chunk?.chunkNo ?? chunk?.sequence ?? index;
  const n = Number(raw);
  return Number.isFinite(n) ? n : index;
}

export function prepareCanonicalDatasetForPersistence(dataset, modelId, metadata = {}) {
  const id = String(modelId || dataset?.model?.id || '').trim();
  if (!id) throw new Error('Cannot persist canonical dataset without model id');

  const prepared = {
    ...dataset,
    model: {
      ...(dataset?.model || {}),
      id,
    },
    metadata: {
      ...(dataset?.metadata || {}),
      persistence: {
        ...(dataset?.metadata?.persistence || {}),
        ...metadata,
      },
    },
  };

  const result = validateCanonicalDataset(prepared);
  if (!result.valid) throw new Error(`Cannot persist invalid canonical dataset: ${result.errors.join('; ')}`);
  return prepared;
}

export function encodeCanonicalDatasetForStorage(dataset, metadata = {}) {
  const json = serializeCanonicalDataset(dataset, metadata);
  const compressed = pako.deflate(json);
  return uint8ArrayToBase64(compressed);
}

export function decodeCanonicalDatasetFromStorage(base64) {
  const bytes = base64ToUint8Array(base64);
  const json = pako.inflate(bytes, { to: 'string' });
  return deserializeCanonicalDataset(json);
}

export async function saveCanonicalDatasetToDatabase({
  modelId,
  dataset,
  appVersion,
  invoke = null,
  snApi = null,
  chunkSize = DEFAULT_CHUNK_SIZE,
  metadata = {},
} = {}) {
  if (!dataset) return { saved: false, reason: 'no-canonical-dataset' };

  const storageId = getCanonicalStorageId(modelId);
  const prepared = prepareCanonicalDatasetForPersistence(dataset, modelId, {
    savedAt: new Date().toISOString(),
    storageId,
    ...metadata,
  });
  const encoded = encodeCanonicalDatasetForStorage(prepared, {
    modelId: String(modelId),
    storageId,
    platform: prepared?.source?.platform || '',
  });
  const chunks = chunkString(encoded, chunkSize);

  if (appVersion === 'FORGE') {
    if (!invoke) throw new Error('Forge invoke is required to persist canonical dataset');
    await invoke('deleteEventLog', { id: storageId }).catch(() => undefined);
    for (let i = 0; i < chunks.length; i++) {
      await invoke('createEventLog', { id: storageId, chunk_no: i, event_log: chunks[i] });
    }
  } else if (appVersion === 'SNOW') {
    if (!snApi) throw new Error('ServiceNow API is required to persist canonical dataset');
    await snApi.deleteEventLogChunks(storageId).catch(() => undefined);
    for (let i = 0; i < chunks.length; i++) {
      await snApi.createEventLogChunk(storageId, i, chunks[i]);
    }
  } else {
    throw new Error(`Unsupported appVersion for canonical persistence: ${appVersion || '(missing)'}`);
  }

  console.log(`[PA Canonical Persistence] saved ${prepared.cases.length} cases / ${prepared.events.length} events in ${chunks.length} chunk(s) as ${storageId}`);
  return {
    saved: true,
    storageId,
    chunks: chunks.length,
    encodedBytes: encoded.length,
    dataset: prepared,
  };
}

export async function loadCanonicalDatasetFromDatabase({
  modelId,
  appVersion,
  invoke = null,
  snApi = null,
} = {}) {
  const storageId = getCanonicalStorageId(modelId);
  let rows = [];

  try {
    if (appVersion === 'FORGE') {
      if (!invoke) throw new Error('Forge invoke is required to load canonical dataset');
      rows = await invoke('getEventLog', { modelID: storageId });
    } else if (appVersion === 'SNOW') {
      if (!snApi) throw new Error('ServiceNow API is required to load canonical dataset');
      rows = await snApi.getEventLog(storageId);
    } else {
      return { found: false, storageId, dataset: null };
    }
  } catch (error) {
    console.warn('[PA Canonical Persistence] canonical dataset lookup failed; legacy reconstruction will be used', error?.message || error);
    return { found: false, storageId, dataset: null, error };
  }

  if (!Array.isArray(rows) || rows.length === 0) return { found: false, storageId, dataset: null };

  try {
    const encoded = [...rows]
      .sort((a, b) => chunkNumber(a, 0) - chunkNumber(b, 0))
      .map(normaliseChunkPayload)
      .join('');
    if (!encoded) return { found: false, storageId, dataset: null };

    const dataset = decodeCanonicalDatasetFromStorage(encoded);
    console.log(`[PA Canonical Persistence] loaded ${dataset.cases.length} cases / ${dataset.events.length} events from ${storageId}`);
    return { found: true, storageId, dataset, chunks: rows.length };
  } catch (error) {
    console.warn('[PA Canonical Persistence] persisted canonical payload is unreadable; legacy reconstruction will be used', error?.message || error);
    return { found: false, storageId, dataset: null, error };
  }
}
